import React, { useState, useEffect, useMemo } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    CheckCircle,
    XCircle,
    Clock,
    AlertCircle,
    MousePointer2,
    CheckSquare
} from 'lucide-react';
import { collection, getDocs, doc, setDoc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { AttendanceRecord } from '../../types/attendance';
import { SchoolTime } from '../../types/schedule';
import { getSchoolTimes } from '../../utils/schedule';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Input from '../ui/Input';

interface CalendarAttendanceViewProps {
    selectedMember: any;
    onBack: () => void;
}

type AttendanceStatus = 'present' | 'absent' | 'late' | 'half-day' | 'leave' | 'holiday' | null;

const CalendarAttendanceView: React.FC<CalendarAttendanceViewProps> = ({
    selectedMember,
    onBack
}) => {
    const { showSuccess, showError } = useToast();

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date(2025, 11, 1)); // December 2025 (month is 0-indexed, so 11 = December)
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedDates, setSelectedDates] = useState<Date[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<AttendanceStatus>(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

    // Attendance data
    const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // School time configuration
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
    const [calendarConfig, setCalendarConfig] = useState<Record<string, any>>({});

    // Holiday work tracking
    const [showHolidayWorkModal, setShowHolidayWorkModal] = useState(false);
    const [holidayWorkReason, setHolidayWorkReason] = useState('');

    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Generate calendar days
    const calendarDays = useMemo(() => {
        const year = currentYear;
        const month = currentMonth;

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push(date);
        }

        return days;
    }, [currentYear, currentMonth]);

    useEffect(() => {
        fetchAttendanceRecords();
        fetchSchoolTimes();
    }, [selectedMember, currentYear, currentMonth]);

    useEffect(() => {
        fetchCalendarConfig();
    }, [currentYear, currentMonth]);

    const fetchAttendanceRecords = async () => {
        if (!selectedMember) return;

        setLoading(true);
        try {
            const employeeId = selectedMember.type === 'staff' ? selectedMember.staffId : selectedMember.teacherId;
            if (!employeeId) {
                setAttendanceRecords([]);
                setLoading(false);
                return;
            }

            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);

            // Reference: attendance/{type}/{employeeId}/{yearMonth}
            const yearMonth = `${year}_${monthName}`;
            const monthRef = doc(db, 'attendance', selectedMember.type, employeeId, yearMonth);
            const monthSnap = await getDoc(monthRef);

            if (monthSnap.exists()) {
                const monthData = monthSnap.data();
                const records: AttendanceRecord[] = [];

                // Process each day in the month
                Object.keys(monthData).forEach(dayStr => {
                    const dayData = monthData[dayStr];
                    if (dayData && typeof dayData === 'object') {
                        const day = parseInt(dayStr);
                        const date = new Date(currentYear, currentMonth, day);

                        // Extract status and times
                        let status: 'present' | 'absent' | 'late' | 'half-day' | 'leave';
                        let checkIn: Date | null = null;
                        let checkOut: Date | null = null;

                        if (dayData.P) {
                            status = 'present';
                            if (dayData.checkIn_time) {
                                const [hours, minutes] = dayData.checkIn_time.split(':');
                                checkIn = new Date(date);
                                checkIn.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                            }
                            if (dayData.checkout_time) {
                                const [hours, minutes] = dayData.checkout_time.split(':');
                                checkOut = new Date(date);
                                checkOut.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                            }
                        } else if (dayData.A) {
                            status = 'absent';
                        } else if (dayData.L) {
                            status = 'leave';
                        } else {
                            return; // Skip invalid entries
                        }

                        records.push({
                            id: `${employeeId}_${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${dayStr.padStart(2, '0')}`,
                            employeeId: selectedMember.id,
                            employeeType: selectedMember.type,
                            date: date,
                            checkIn: checkIn,
                            checkOut: checkOut,
                            status: status,
                            workingHours: 0,
                            overtimeHours: 0,
                            notes: dayData.reason || '',
                            isHolidayWork: dayData.isHolidayWork === true,
                            holidayWorkReason: dayData.holidayWorkReason || '',
                            createdAt: new Date(),
                            updatedAt: new Date()
                        });
                    }
                });

                setAttendanceRecords(records);
            } else {
                setAttendanceRecords([]);
            }
        } catch (error) {
            console.error('Error fetching attendance records:', error);
            showError('Error', 'Failed to fetch attendance records');
        } finally {
            setLoading(false);
        }
    };

    const fetchSchoolTimes = async () => {
        try {
            const data = await getSchoolTimes();
            setSchoolTimes(data);
        } catch (error) {
            console.error('Error fetching school times:', error);
            showError('Error', 'Failed to fetch school time configuration');
        }
    };

    const fetchCalendarConfig = async () => {
        try {
            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);
            const configRef = doc(db, 'calendarConfig', `${year}_${monthName}`);
            const configSnap = await getDoc(configRef);

            if (configSnap.exists()) {
                setCalendarConfig(configSnap.data());
            } else {
                setCalendarConfig({});
            }
        } catch (error) {
            console.error('Error fetching calendar config:', error);
            showError('Error', 'Failed to fetch calendar configuration');
        }
    };

    // Check if a day is a holiday based on school time configuration and custom holidays
    const isHoliday = (date: Date): boolean => {
        // Check for custom holidays first
        const dateStr = date.toISOString().split('T')[0];
        const dayStr = date.getDate().toString();

        if (calendarConfig[dayStr] && calendarConfig[dayStr].isHoliday) {
            return true;
        }

        // Check for school holidays
        if (schoolTimes.length === 0) return false;

        const activeSchoolTime = schoolTimes.find(st => st.isActive);
        if (!activeSchoolTime) return false;

        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const dayConfig = activeSchoolTime.days.find(day => day.day === dayName);

        return dayConfig ? !dayConfig.isActive : false;
    };

    const isBeforeDecember2025 = (date: Date): boolean => {
        const dec2025 = new Date(2025, 11, 1); // December 2025 (month is 0-indexed)
        const checkYear = date.getFullYear();
        const checkMonth = date.getMonth();

        return checkYear < 2025 || (checkYear === 2025 && checkMonth < 11);
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentDate);
        if (direction === 'prev') {
            newDate.setMonth(newDate.getMonth() - 1);
            // Don't allow navigation before December 2025
            if (isBeforeDecember2025(newDate)) {
                return;
            }
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
    };

    const getAttendanceForDate = (date: Date): AttendanceRecord | null => {
        const dateStr = date.toDateString();
        return attendanceRecords.find(record =>
            record.date.toDateString() === dateStr
        ) || null;
    };

    const isDateDisabled = (date: Date): boolean => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Disable future dates
        if (date > today) {
            return true;
        }

        // Disable dates before December 2025
        const dec2025 = new Date(2025, 11, 1); // December 1, 2025
        dec2025.setHours(0, 0, 0, 0);
        const dateCopy = new Date(date);
        dateCopy.setHours(0, 0, 0, 0);

        return dateCopy < dec2025;
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'present':
                return <CheckCircle className="h-4 w-4 text-green-600" />;
            case 'absent':
                return <XCircle className="h-4 w-4 text-red-600" />;
            case 'leave':
                return <AlertCircle className="h-4 w-4 text-blue-600" />;
            default:
                return <Clock className="h-4 w-4 text-gray-400" />;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'present':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'absent':
                return 'bg-red-100 text-red-800 border-red-200';
            case 'leave':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            default:
                return 'bg-gray-100 text-gray-600 border-gray-200';
        }
    };

    const handleDateClick = (date: Date) => {
        if (isDateDisabled(date)) return;

        if (isHoliday(date)) {
            // Check if there's already holiday work for this date
            const existingRecord = getAttendanceForDate(date);
            if (existingRecord && existingRecord.isHolidayWork) {
                // Already marked as holiday work, show read-only modal with reason
                setSelectedDate(date);
                setHolidayWorkReason(existingRecord.holidayWorkReason || '');
                setShowHolidayWorkModal(true);
            } else {
                // Show holiday work modal for new holiday work
                setSelectedDate(date);
                setHolidayWorkReason('');
                setShowHolidayWorkModal(true);
            }
            return;
        }

        if (isMultiSelectMode) {
            // Multi-select mode: add/remove date from selection
            setSelectedDates(prev => {
                const isSelected = prev.some(d => d.toDateString() === date.toDateString());
                if (isSelected) {
                    return prev.filter(d => d.toDateString() !== date.toDateString());
                } else {
                    return [...prev, date];
                }
            });
        } else {
            // Single select mode: open status modal
            setSelectedDate(date);
            const existingRecord = getAttendanceForDate(date);
            setSelectedStatus(existingRecord?.status || null);
            setShowStatusModal(true);
        }
    };

    const handleStatusSave = async () => {
        if (!selectedStatus || !selectedMember) return;

        const datesToProcess = isMultiSelectMode ? selectedDates : (selectedDate ? [selectedDate] : []);
        if (datesToProcess.length === 0) return;

        setSaving(true);
        try {
            const employeeId = selectedMember.type === 'staff' ? selectedMember.staffId : selectedMember.teacherId;
            if (!employeeId) {
                showError('Error', 'Employee ID not found');
                return;
            }

            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);

            // Reference: attendance/{type}/{employeeId}/{yearMonth}
            const yearMonth = `${year}_${monthName}`;
            const monthRef = doc(db, 'attendance', selectedMember.type, employeeId, yearMonth);
            const monthSnap = await getDoc(monthRef);

            let monthData: any = {};
            if (monthSnap.exists()) {
                monthData = monthSnap.data();
            }

            // Process each selected date
            for (const date of datesToProcess) {
                const day = date.getDate().toString();

                // Create day data based on status
                let dayData: any = {};
                switch (selectedStatus) {
                    case 'present':
                        dayData = {
                            P: true,
                            checkIn_time: '09:00', // Default check-in time
                            checkout_time: '17:00' // Default check-out time
                        };
                        break;
                    case 'absent':
                        dayData = { A: true };
                        break;
                    case 'leave':
                        dayData = {
                            L: true,
                            reason: 'Personal leave' // Default reason
                        };
                        break;
                }

                // Update the specific day
                monthData[day] = dayData;
            }

            // Save to Firestore
            await setDoc(monthRef, monthData, { merge: true });

            const message = isMultiSelectMode
                ? `Attendance marked for ${datesToProcess.length} days successfully!`
                : 'Attendance saved successfully!';

            showSuccess('Success', message);
            setShowStatusModal(false);
            setSelectedDates([]);
            setIsMultiSelectMode(false);
            fetchAttendanceRecords();
        } catch (error) {
            console.error('Error saving attendance:', error);
            showError('Error', 'Failed to save attendance');
        } finally {
            setSaving(false);
        }
    };

    const handleHolidayWorkSave = async () => {
        if (!selectedDate || !selectedMember || !holidayWorkReason.trim()) {
            showError('Error', 'Please provide a reason for holiday work');
            return;
        }

        setSaving(true);
        try {
            const employeeId = selectedMember.type === 'staff' ? selectedMember.staffId : selectedMember.teacherId;
            if (!employeeId) {
                showError('Error', 'Employee ID not found');
                return;
            }

            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);

            // Reference: attendance/{type}/{employeeId}/{yearMonth}
            const yearMonth = `${year}_${monthName}`;
            const monthRef = doc(db, 'attendance', selectedMember.type, employeeId, yearMonth);
            const monthSnap = await getDoc(monthRef);

            let monthData: any = {};
            if (monthSnap.exists()) {
                monthData = monthSnap.data();
            }

            const day = selectedDate.getDate().toString();

            // Create day data for holiday work
            const dayData = {
                P: true,
                checkIn_time: '09:00', // Default check-in time
                checkout_time: '17:00', // Default check-out time
                isHolidayWork: true,
                holidayWorkReason: holidayWorkReason.trim()
            };

            // Update the specific day
            monthData[day] = dayData;

            // Save to Firestore
            await setDoc(monthRef, monthData, { merge: true });

            showSuccess('Success', 'Holiday work marked successfully!');
            setShowHolidayWorkModal(false);
            setSelectedDate(null);
            setHolidayWorkReason('');
            fetchAttendanceRecords();
        } catch (error) {
            console.error('Error saving holiday work:', error);
            showError('Error', 'Failed to save holiday work');
        } finally {
            setSaving(false);
        }
    };

    const getMonthName = (month: number) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[month];
    };

    const getDayName = (day: number) => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return days[day];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <CalendarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Attendance Calendar</h2>
                        <p className="text-gray-600">
                            {selectedMember.firstName} {selectedMember.lastName} - {selectedMember.type.charAt(0).toUpperCase() + selectedMember.type.slice(1)}
                        </p>
                    </div>
                </div>
                <div className="flex items-center space-x-3">
                    <Button
                        variant={isMultiSelectMode ? "primary" : "secondary"}
                        onClick={() => {
                            setIsMultiSelectMode(!isMultiSelectMode);
                            setSelectedDates([]);
                        }}
                        className="flex items-center space-x-2"
                    >
                        {isMultiSelectMode ? (
                            <>
                                <CheckSquare className="h-4 w-4" />
                                <span>Multi-Select Mode</span>
                            </>
                        ) : (
                            <>
                                <MousePointer2 className="h-4 w-4" />
                                <span>Enable Multi-Select</span>
                            </>
                        )}
                    </Button>
                    <Button variant="secondary" onClick={onBack}>
                        Back
                    </Button>
                </div>
            </div>

            {/* Month Navigation */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <Button
                            variant="secondary"
                            onClick={() => navigateMonth('prev')}
                            className="p-2"
                            disabled={isBeforeDecember2025(currentDate)}
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>

                        <div className="text-center">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {getMonthName(currentMonth)} {currentYear}
                            </h3>
                        </div>

                        <Button
                            variant="secondary"
                            onClick={() => navigateMonth('next')}
                            className="p-2"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Calendar Grid */}
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                                <p className="text-sm text-gray-600">Loading attendance data...</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-7 gap-1">
                            {/* Day Headers */}
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                                    {day}
                                </div>
                            ))}

                            {/* Calendar Days */}
                            {calendarDays.map((date, index) => {
                                if (!date) {
                                    return <div key={index} className="p-2"></div>;
                                }

                                const attendance = getAttendanceForDate(date);
                                const isDisabled = isDateDisabled(date);
                                const isHolidayDay = isHoliday(date);
                                const isToday = date.toDateString() === new Date().toDateString();
                                const isSelected = selectedDate?.toDateString() === date.toDateString();
                                const isMultiSelected = isMultiSelectMode && selectedDates.some(d => d.toDateString() === date.toDateString());

                                return (
                                    <div
                                        key={date.toDateString()}
                                        className={`
                                        p-2 min-h-[60px] border rounded-lg cursor-pointer transition-all duration-200
                                        ${isDisabled || isHolidayDay
                                                ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                                                : isMultiSelectMode
                                                    ? 'hover:bg-blue-50 hover:border-blue-300'
                                                    : 'hover:bg-gray-50 hover:border-emerald-300'
                                            }
                                        ${isHolidayDay ? 'bg-orange-100 border-orange-300' : ''}
                                        ${isToday ? 'ring-2 ring-emerald-500' : ''}
                                        ${isSelected ? 'bg-emerald-100 border-emerald-500' : ''}
                                        ${isMultiSelected ? 'bg-blue-100 border-blue-500 ring-2 ring-blue-300' : ''}
                                        ${attendance ? `border-2 ${getStatusColor(attendance.status).split(' ')[2]}` : ''}
                                    `}
                                        onClick={() => handleDateClick(date)}
                                    >
                                        <div className="flex flex-col items-center space-y-1">
                                            <span className={`text-sm font-medium ${isDisabled || isHolidayDay ? 'text-gray-400' : 'text-gray-900'
                                                }`}>
                                                {date.getDate()}
                                            </span>

                                            {isHolidayDay && !attendance && (
                                                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-orange-100">
                                                    <span className="text-orange-600 font-bold text-xs">H</span>
                                                </div>
                                            )}

                                            {attendance && !isDisabled && (
                                                <div className={`flex items-center justify-center w-6 h-6 rounded-full ${getStatusColor(attendance.status)}`}>
                                                    {attendance.isHolidayWork ? (
                                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                                    ) : (
                                                        getStatusIcon(attendance.status)
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </Card>

            {/* Legend */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Legend & Instructions</h3>

                    {/* Status Legend */}
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Attendance Status:</h4>
                        <div className="flex flex-wrap gap-4">
                            <div className="flex items-center space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-600" />
                                <span className="text-sm text-gray-600">Present</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <XCircle className="h-4 w-4 text-red-600" />
                                <span className="text-sm text-gray-600">Absent</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <AlertCircle className="h-4 w-4 text-blue-600" />
                                <span className="text-sm text-gray-600">Leave</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 rounded-full bg-orange-100 flex items-center justify-center">
                                    <span className="text-orange-600 font-bold text-xs">H</span>
                                </div>
                                <span className="text-sm text-gray-600">Holiday</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center">
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                </div>
                                <span className="text-sm text-gray-600">Holiday Work</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Clock className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-600">Not Marked</span>
                            </div>
                        </div>
                    </div>

                    {/* Instructions */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">How to use:</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                            <p>• <strong>Single Mode:</strong> Click any date to mark attendance for that day</p>
                            <p>• <strong>Multi-Select Mode:</strong> Enable multi-select, click multiple dates, then mark attendance for all selected days</p>
                            <p>• <strong>Future dates</strong> and <strong>holidays</strong> are disabled and cannot be selected</p>
                            <p>• <strong>Holidays (H)</strong> are automatically determined from school time configuration</p>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Multi-Select Action Button */}
            {isMultiSelectMode && selectedDates.length > 0 && (
                <Card glassy={true} className="islamic-pattern">
                    <div className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {selectedDates.length} days selected
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Click to mark attendance for all selected dates
                                </p>
                            </div>
                            <Button
                                onClick={() => setShowStatusModal(true)}
                                className="flex items-center space-x-2"
                                disabled={saving}
                                loading={saving}
                            >
                                <CheckSquare className="h-4 w-4" />
                                <span>{saving ? 'Processing...' : 'Mark Attendance'}</span>
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            {/* Status Selection Modal */}
            {showStatusModal && (
                <Modal
                    isOpen={true}
                    onClose={() => {
                        setShowStatusModal(false);
                        if (isMultiSelectMode) {
                            setSelectedDates([]);
                            setIsMultiSelectMode(false);
                        }
                    }}
                    title={isMultiSelectMode
                        ? `Mark Attendance - ${selectedDates.length} days`
                        : `Mark Attendance - ${selectedDate?.toLocaleDateString()}`
                    }
                    size="sm"
                >
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Select Attendance Status
                            </h3>
                            <p className="text-sm text-gray-600">
                                {selectedMember.firstName} {selectedMember.lastName}
                            </p>
                            {isMultiSelectMode ? (
                                <div className="mt-2">
                                    <p className="text-sm text-blue-600 font-medium">
                                        Multi-Select Mode: {selectedDates.length} days selected
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Selected dates: {selectedDates.map(d => d.getDate()).join(', ')}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">
                                    {selectedDate?.toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                            )}
                        </div>

                        <div className="space-y-3">
                            {[
                                { value: 'present', label: 'Present', icon: CheckCircle, color: 'text-green-600' },
                                { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-red-600' },
                                { value: 'leave', label: 'Leave', icon: AlertCircle, color: 'text-blue-600' }
                            ].map((status) => {
                                const Icon = status.icon;
                                const isSelected = selectedStatus === status.value;

                                return (
                                    <button
                                        key={status.value}
                                        onClick={() => setSelectedStatus(status.value as AttendanceStatus)}
                                        className={`w-full p-4 border-2 rounded-lg transition-all duration-200 ${isSelected
                                            ? 'border-emerald-500 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon className={`h-6 w-6 ${status.color}`} />
                                            <span className="font-medium text-gray-900">{status.label}</span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowStatusModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleStatusSave}
                                disabled={!selectedStatus || saving}
                                loading={saving}
                            >
                                {saving
                                    ? 'Saving...'
                                    : isMultiSelectMode
                                        ? `Mark ${selectedDates.length} Days`
                                        : 'Save Attendance'
                                }
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Holiday Work Modal */}
            {showHolidayWorkModal && selectedDate && (() => {
                const existingRecord = getAttendanceForDate(selectedDate);
                const isExistingHolidayWork = Boolean(existingRecord && existingRecord.isHolidayWork);

                return (
                    <Modal
                        isOpen={true}
                        onClose={() => {
                            setShowHolidayWorkModal(false);
                            setSelectedDate(null);
                            setHolidayWorkReason('');
                        }}
                        title={isExistingHolidayWork
                            ? `Holiday Work Details - ${selectedDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}`
                            : `Mark Holiday Work - ${selectedDate.toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}`
                        }
                        size="md"
                    >
                        <div className="space-y-6">
                            <div className="text-center">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">
                                    {isExistingHolidayWork ? 'Holiday Work Details' : 'Mark Holiday Work'}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {selectedMember.firstName} {selectedMember.lastName}
                                </p>
                                <p className="text-sm text-gray-500">
                                    {selectedDate.toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </p>
                                <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-sm text-orange-700">
                                        {isExistingHolidayWork
                                            ? 'This day was marked as holiday work. Details are shown below.'
                                            : 'This is a holiday. Working on this day will be tracked separately for salary calculation.'
                                        }
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Reason for Holiday Work *
                                    </label>
                                    <Input
                                        type="text"
                                        value={holidayWorkReason}
                                        onChange={(e) => setHolidayWorkReason(e.target.value)}
                                        placeholder="e.g., Emergency maintenance, Special event, etc."
                                        required
                                        disabled={isExistingHolidayWork}
                                        className={isExistingHolidayWork ? 'bg-gray-100 cursor-not-allowed' : ''}
                                    />
                                </div>

                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-medium text-blue-900 mb-2">Important Notes:</h4>
                                    <ul className="text-sm text-blue-700 space-y-1">
                                        <li>• Holiday work will be counted as present attendance</li>
                                        <li>• Extra payment will be calculated separately</li>
                                        <li>• This information will be used for salary finalization</li>
                                        <li>• Default working hours: 9:00 AM - 5:00 PM</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setShowHolidayWorkModal(false);
                                        setSelectedDate(null);
                                        setHolidayWorkReason('');
                                    }}
                                >
                                    {isExistingHolidayWork ? 'Close' : 'Cancel'}
                                </Button>
                                {!isExistingHolidayWork && (
                                    <Button
                                        onClick={handleHolidayWorkSave}
                                        disabled={!holidayWorkReason.trim() || saving}
                                        loading={saving}
                                    >
                                        {saving ? 'Saving...' : 'Mark Holiday Work'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </Modal>
                );
            })()}
        </div>
    );
};

export default CalendarAttendanceView;
